import styled from '@emotion/styled';
import { useEditorState } from '@tiptap/react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaLink } from 'react-icons/fa';
import * as Yup from 'yup';

import SubmitButton from 'Shared/form/SubmitButton';
import { TextField } from 'Shared/form/TextField';

const StyledPopover = styled(Popover)`
    width: 500px;
    max-width: none;
`;

const ButtonRowStyle = styled.div`
    display: flex;
    justify-content: space-between;
`;

const InsertLinkDropdown = ({ editor }) => {
    const editorState = useEditorState({
        editor,
        selector: (ctx) => ({
            selection: ctx.editor.editorState.selection,
            doc: ctx.editor.editorState.doc,
            isLink: ctx.editor.isActive('link') ?? false,
            linkHref: ctx.editor.getAttributes('link').href,
        }),
    });

    const popover = (
        <StyledPopover id="tiptap-link-popover">
            <Popover.Title>Insert link</Popover.Title>
            <Popover.Content>
                <Formik
                    initialValues={{
                        text: editorState.doc.textBetween(editorState.selection.from, editorState.selection.to),
                        url: editorState.linkHref,
                    }}
                    validationSchema={Yup.object().shape({
                        url: Yup.string().url(),
                    })}
                    onSubmit={(values) => {
                        const { text, url } = values;
                        const { from, to } = editorState.selection;
                        editor.chain().focus().setLink({ href: url }).insertContentAt({ from, to }, text).run();
                        document.body.click();
                    }}
                >
                    {({ handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                            <TextField fieldName="url" label="URL" required />
                            <TextField fieldName="text" label="Link text" required />

                            <ButtonRowStyle>
                                <SubmitButton>Insert</SubmitButton>
                                {editorState.isLink && (
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            editor.chain().focus().unsetLink().run();
                                            document.body.click();
                                        }}
                                    >
                                        Remove link
                                    </Button>
                                )}
                            </ButtonRowStyle>
                        </form>
                    )}
                </Formik>
            </Popover.Content>
        </StyledPopover>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose>
            <Button
                size="xs"
                disabled={!editor.isEditable}
                className={editorState.isLink ? 'is-active' : ''}
                aria-label="Insert link"
            >
                <FaLink />
            </Button>
        </OverlayTrigger>
    );
};

InsertLinkDropdown.propTypes = {
    editor: PropTypes.shape({
        chain: PropTypes.func,
        isEditable: PropTypes.bool,
    }).isRequired,
};

export default InsertLinkDropdown;
